import React, { useEffect, useRef } from 'react';

/**
 * Scroll-driven hero: fragments of a year, scattered across the apps they
 * currently live in, resolving into one weekday-aligned picture.
 *
 * The layout underneath is the real annual view — a month gutter plus 37 day
 * columns, because months are weekday-aligned rather than numbered from 1 (up
 * to six leading offset cells + 31 days). That alignment is why a weekly
 * commitment lands as a straight vertical line once assembled.
 *
 * Progress is written straight to the DOM rather than through React state:
 * re-rendering ~100 tiles on every scroll frame would drop frames for nothing.
 */

const YEAR = 2026;

/** Designed jewel set. NOT the app's category colours — those are a user
 *  preference and must never stand in for brand. */
const HUES = ['#E0A22B', '#E4664F', '#2FA39B', '#7B62C9', '#8FB33E', '#4A8FD4'];

type Raw = [string, number, [number, number], [number, number]];

const EVENTS: Raw[] = [
  ['Site relaunch', 3, [1, 2], [4, 15]],      ['Budget week', 3, [0, 19], [0, 23]],
  ['Product launch', 3, [4, 18], [4, 22]],    ['Hiring', 3, [5, 8], [5, 12]],
  ['Q3 planning', 3, [5, 22], [6, 10]],       ['Autumn planning', 3, [8, 14], [8, 25]],
  ['Design sprint', 3, [9, 5], [9, 16]],      ['Team offsite', 3, [10, 4], [10, 6]],
  ['Half term', 1, [1, 16], [1, 20]],         ['School holidays', 1, [6, 6], [7, 30]],
  ['Grandparents', 1, [4, 28], [5, 2]],       ['Back to school', 1, [8, 1], [8, 4]],
  ['Family at ours', 1, [11, 24], [11, 28]],  ['Mum’s birthday', 1, [3, 18], [3, 18]],
  ['Ski week', 5, [0, 24], [0, 31]],          ['Lisbon', 5, [2, 12], [2, 16]],
  ['Faro', 5, [7, 1], [7, 16]],               ['Porto', 5, [8, 18], [8, 21]],
  ['Berlin', 5, [9, 21], [9, 24]],            ['Xmas market', 5, [11, 11], [11, 14]],
  ['Physio', 2, [10, 10], [10, 21]],          ['Marathon', 2, [3, 26], [3, 26]],
  ['Easter', 0, [3, 3], [3, 6]],              ['Christmas', 0, [11, 25], [11, 26]],
];

const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
const daysIn = (m: number) => new Date(YEAR, m + 1, 0).getDate();
const firstDay = (m: number) => new Date(YEAR, m, 1).getDay();

interface Ev { t: string; h: number; from: [number, number]; to: [number, number] }

function allEvents(): Ev[] {
  const out: Ev[] = EVENTS.map(e => ({ t: e[0], h: e[1], from: e[2], to: e[3] }));
  for (let m = 0; m < 12; m++) {
    for (let d = 1; d <= daysIn(m); d++) {
      if (new Date(YEAR, m, d).getDay() === 2) out.push({ t: 'Gym', h: 4, from: [m, d], to: [m, d] });
    }
  }
  return out;
}

/** Seeded so the scattered composition is identical on every load. */
const rnd = (i: number, s: number) => {
  const x = Math.sin(i * 127.1 + s * 311.7) * 43758.5453;
  return x - Math.floor(x);
};
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const COPY: Array<[number, string, string]> = [
  [0.00, 'Every piece of your year,|scattered', 'Work in one calendar. Family in another. The trip in an email, the deadline in a task list.'],
  [0.45, 'The pieces were always|the same year', 'They just never sat on the same screen.'],
  [0.82, 'One picture,|all of it', 'Twelve months, weekday-aligned. Nothing hidden behind a scroll.'],
];

interface Tile {
  el: HTMLDivElement;
  i: number;
  dx: number; dy: number; rot: number; sc: number; sx: number;
}

export default function MosaicHero({ children }: { children?: React.ReactNode }) {
  const zoneRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const swapTimer = useRef<number | undefined>(undefined);
  const h2Ref = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const tilesRef = useRef<Tile[]>([]);
  const staticsRef = useRef<HTMLElement[]>([]);
  const copyKey = useRef('');

  useEffect(() => {
    const frame = frameRef.current, canvas = canvasRef.current, zone = zoneRef.current;
    if (!frame || !canvas || !zone) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ALL = allEvents();

    const build = () => {
      const r = frame.getBoundingClientRect();
      const W = r.width, H = r.height;
      canvas.innerHTML = '';
      tilesRef.current = [];
      staticsRef.current = [];

      const padX = Math.max(20, W * 0.045);
      const padTop = 40;
      const padBottom = Math.min(280, H * 0.46);
      // A phone cannot carry a full month of day columns: 37 across 375px is
      // about 9px each, so a five-day event renders as a sliver. Show two weeks
      // instead — 14 keeps whole weeks, so the columns still read SUN..SAT twice
      // and weekends still stripe down all twelve months. Months whose 1st falls
      // late in the week show fewer days before the window runs out; the ragged
      // right edge is inherent to weekday alignment.
      const cols = W < 640 ? 14 : 37;
      // The gutter must clear the month label, or the bars start underneath it.
      // "MAR" is ~20px at 8.5px type, so 18 was too tight; with only 14 columns
      // there is room to spare.
      const gutter = 28;
      const colW = (W - padX * 2 - gutter) / cols;

      const months = [];
      for (let m = 0; m < 12; m++) {
        const dim = daysIn(m), off = firstDay(m);
        const segs: Array<{ ev: Ev; start: number; end: number; lane: number }> = [];
        for (const ev of ALL) {
          const [fm, fd] = ev.from, [tm, td] = ev.to;
          if (fm > m || tm < m) continue;
          segs.push({ ev, start: fm === m ? fd : 1, end: tm === m ? td : dim, lane: 0 });
        }
        segs.sort((a, b) => (a.start - b.start) || ((b.end - b.start) - (a.end - a.start)));
        const lanes: Array<Array<[number, number]>> = [];
        for (const s of segs) {
          let l = 0;
          for (;;) {
            const occ = lanes[l] || (lanes[l] = []);
            if (occ.every(([a, b]) => s.end < a || s.start > b)) { occ.push([s.start, s.end]); break; }
            l++;
          }
          s.lane = l;
        }
        months.push({ m, off, segs, lanes: Math.max(lanes.length, 1) });
      }

      const totalLanes = months.reduce((n, x) => n + x.lanes, 0);
      const laneH = Math.max(7, Math.min(15, (H - padTop - padBottom - 12 * 9) / totalLanes));
      const headH = 9;

      let y = padTop;
      for (const mo of months) {
        const tag = document.createElement('div');
        tag.className = 'mh-tag';
        tag.textContent = MONTHS[mo.m];
        tag.style.cssText = `left:${padX}px;top:${y + 1}px`;
        canvas.appendChild(tag);
        staticsRef.current.push(tag);
        y += headH + mo.lanes * laneH + 9;
      }
      const gridBottom = y - 9;
      for (let i = 0; i < cols; i++) {
        if (i % 7 !== 0 && i % 7 !== 6) continue;
        const s = document.createElement('div');
        s.className = 'mh-stripe';
        s.style.cssText =
          `left:${padX + gutter + i * colW}px;top:${padTop}px;width:${colW}px;height:${gridBottom - padTop}px`;
        canvas.appendChild(s);
        staticsRef.current.push(s);
      }

      let idx = 0;
      y = padTop;
      for (const mo of months) {
        for (const s of mo.segs) {
          // Clip to the visible window: skip bars that begin past it, truncate
          // those that run through it.
          const startSlot = mo.off + s.start - 1;
          if (startSlot >= cols) continue;
          const endSlot = Math.min(mo.off + s.end - 1, cols - 1);
          const x = padX + gutter + startSlot * colW;
          const w = Math.max(colW * (endSlot - startSlot + 1) - 1.5, 3);
          const ty = y + headH + s.lane * laneH;
          const h = Math.max(laneH - 2.5, 5);

          const el = document.createElement('div');
          el.className = 'mh-tile';
          el.textContent = (s.end - s.start >= 2 || s.ev.t.length <= 3) ? s.ev.t : '';
          el.style.cssText =
            `left:${x}px;top:${ty}px;width:${w}px;height:${h}px;line-height:${h}px;background:${HUES[s.ev.h]}`;
          canvas.appendChild(el);

          tilesRef.current.push({
            el, i: idx,
            dx: (rnd(idx, 1) - .5) * W * 1.15,
            dy: (rnd(idx, 2) - .5) * H * 1.45,
            rot: (rnd(idx, 3) - .5) * 64,
            sc: .55 + rnd(idx, 4) * .7,
            sx: Math.min(1, 46 / Math.max(w, 1)),
          });
          idx++;
        }
        y += headH + mo.lanes * laneH + 9;
      }

      draw(reduced ? 1 : 0);
    };

    const draw = (p: number) => {
      const tiles = tilesRef.current;
      const n = tiles.length || 1;
      const spread = 0.45;
      for (const t of tiles) {
        const local = Math.max(0, Math.min(1, (p - (t.i / n) * spread) / (1 - spread)));
        const e = easeOut(local);
        const sc = lerp(t.sc, 1, e);
        const sx = lerp(t.sx, 1, e);
        t.el.style.transform =
          `translate(${lerp(t.dx, 0, e).toFixed(1)}px, ${lerp(t.dy, 0, e).toFixed(1)}px) ` +
          `rotate(${lerp(t.rot, 0, e).toFixed(2)}deg) scale(${(sc * sx).toFixed(3)}, ${sc.toFixed(3)})`;
        t.el.style.opacity = (0.3 + 0.7 * e).toFixed(3);
        t.el.style.boxShadow = `0 0 ${lerp(18, 5, e).toFixed(0)}px rgba(255,255,255,${lerp(.08, .02, e).toFixed(3)})`;
        t.el.style.color = `rgba(10,11,13,${sx.toFixed(2)})`;
      }
      const sOp = Math.max(0, Math.min(1, (p - .45) / .35)).toFixed(2);
      for (const s of staticsRef.current) s.style.opacity = sOp;

      let c = COPY[0];
      for (const x of COPY) if (p >= x[0]) c = x;
      if (copyKey.current !== c[1]) {
        copyKey.current = c[1];
        const [a, b] = c[1].split('|');
        // Fade the old line out, swap the text at the bottom of the fade, fade
        // the new one in. Swapping in place reads as a glitch.
        window.clearTimeout(swapTimer.current);
        copyRef.current?.classList.add('swapping');
        swapTimer.current = window.setTimeout(() => {
          if (h2Ref.current) h2Ref.current.innerHTML = `${a}<br><em>${b}</em>`;
          if (subRef.current) subRef.current.textContent = c[2];
          copyRef.current?.classList.remove('swapping');
        }, 170);
      }
    };

    // The stage pins below the sticky nav, so the zone's top starts at NAV_H
    // rather than 0. Without accounting for that, the first NAV_H pixels of
    // scroll produced no movement while sliding the hero out of view — the
    // animation appeared to start late and half-hidden.
    const NAV_H = 64;

    let ticking = false;
    const onScroll = () => {
      if (ticking || reduced) return;
      ticking = true;
      requestAnimationFrame(() => {
        const r = zone.getBoundingClientRect();
        const stageH = window.innerHeight - NAV_H;
        const total = r.height - stageH;
        if (total > 0) draw(Math.max(0, Math.min(1, (NAV_H - r.top) / total)));
        ticking = false;
      });
    };

    let rt: number | undefined;
    const onResize = () => { window.clearTimeout(rt); rt = window.setTimeout(build, 150); };

    build();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.clearTimeout(rt);
      window.clearTimeout(swapTimer.current);
    };
  }, []);

  return (
    <div className="mh-zone" ref={zoneRef}>
      <div className="mh-stage">
        <div className="mh-frame" ref={frameRef}>
          <div
            className="mh-canvas"
            ref={canvasRef}
            role="img"
            aria-label="Fragments of a year — projects, trips, school holidays and a weekly class — assembling into a single twelve-month view"
          />
          <div className="mh-overlay">
            <div className="mh-copy" ref={copyRef}>
            <h1 className="mh-h1" ref={h2Ref}>
              Every piece of your year,<br /><em>scattered</em>
            </h1>
            <p className="mh-sub" ref={subRef}>
              Work in one calendar. Family in another. The trip in an email, the deadline in a task list.
            </p>
            </div>
            <div className="mh-cta">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
